import { Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import { useStaticReflection } from '../../../api/media-sim/reflections';
import { ReflectionCategoryTabs } from './ReflectionCategoryTabs';
import { RegenerateButton } from './RegenerateButton';
import { ProgressBar } from '../shared/ProgressBar';

interface StaticReflectionViewProps {
  publicationId: string;
  isAdmin: boolean;
}

export function StaticReflectionView({ publicationId, isAdmin }: StaticReflectionViewProps) {
  const { theme } = useTheme();
  const { reflection, loading, error, refetch } = useStaticReflection(publicationId);

  if (loading) {
    return (
      <Card variant="elevated">
        <Card.Header title="Publication Style Profile" />
        <Card.Body>
          <Stack gap="md">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 16,
                  borderRadius: 4,
                  backgroundColor: theme.colors.neutral[200],
                  width: `${70 + i * 10}%`,
                }}
              />
            ))}
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  if (error && error.status !== 404) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.semantic.error.default }}>
            {error.message || 'Failed to load reflection'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  // No reflection exists
  if (!reflection || error?.status === 404) {
    return (
      <Card variant="elevated">
        <Card.Header title="Publication Style Profile" />
        <Card.Body>
          <Stack gap="md">
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              No style profile generated yet. A reflection will be automatically created
              when a simulation is run for this publication.
            </p>
            <RegenerateButton
              publicationId={publicationId}
              isAdmin={isAdmin}
              onRegenerated={refetch}
            />
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // Pending state
  if (reflection.status === 'pending') {
    return (
      <Card variant="elevated">
        <Card.Header title="Publication Style Profile" />
        <Card.Body>
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
            Reflection generation queued...
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Running state
  if (reflection.status === 'running') {
    return (
      <Card variant="elevated">
        <Card.Header title="Publication Style Profile" />
        <Card.Body>
          <Stack gap="md">
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
              Generating reflection...
            </p>
            <ProgressBar value={reflection.progress_percent} size="md" showLabel />
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // Failed state
  if (reflection.status === 'failed') {
    return (
      <Card variant="elevated">
        <Card.Header title="Publication Style Profile" />
        <Card.Body>
          <Stack gap="md">
            <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
              Reflection generation failed. An admin can retry.
            </p>
            <RegenerateButton
              publicationId={publicationId}
              isAdmin={isAdmin}
              onRegenerated={refetch}
            />
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // Completed — show full reflection
  return (
    <Card variant="elevated">
      <Card.Header
        title="Publication Style Profile"
        subtitle={
          reflection.last_generated_at
            ? `Generated ${new Date(reflection.last_generated_at).toLocaleDateString()}`
            : undefined
        }
        action={
          <RegenerateButton
            publicationId={publicationId}
            isAdmin={isAdmin}
            onRegenerated={refetch}
          />
        }
      />
      <Card.Body>
        {reflection.consolidated_notes ? (
          <ReflectionCategoryTabs notes={reflection.consolidated_notes} />
        ) : (
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
            Reflection completed but no content available.
          </p>
        )}
      </Card.Body>
    </Card>
  );
}
